import { useEffect, useState } from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Typography,
} from "@mui/material";
import { useAdminUserHook } from '../../Hooks/AdminUserControl/adminUserControlHooks';
import { IconPlus, IconEdit, IconEye, IconTrash } from '@tabler/icons-react';

import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import RouteHelper from '../../Utilities/Helpers/RouteHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';
import StatusHelper from '../../Utilities/Helpers/StatusHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';

import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { AdminUserListField } from '../../Utilities/Enums/ParametersEnum';
import { AdminModules } from '../../Utilities/Enums/AdminModulesEnum';
import { ActiveStatusEnum } from '../../Utilities/Enums/StatusEnums';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';

import { AdminUserList } from '../../Models/AdminUserControl/AdminUserControl';

import CustomListComponent from '../../CustomViews/Pages/ListComponent/CustomListComponent';
import CustomIconButton from '../../CustomViews/Elements/CustomIconButton';
import CustomDeleteConfirmation from '../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomListSearch from '../../CustomViews/Pages/ListComponent/Search/CustomListSearch';
import { TableContainer } from '../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';
import CustomListHeader from '../../CustomViews/Pages/ListComponent/CustomListHeader';
import FormDropdown from '../../CustomViews/Elements/FormDropdown';
import RolesDropdown from '../../CustomViews/Elements/AutoComplete/RolesDropdown';
import { CustomListSearchResetBtns } from '../../CustomViews/Pages/ListComponent/Search/CustomListSearchResetBtns';
import NewTab from '../../CustomViews/Pages/NewTab/NewTab';

export default function AdminUserControlList() {
    const navigate = useNavigate()
    const location = useLocation()

    //search params
    const queryParams = new URLSearchParams(location.search);
    const [searchParams, setSearchParams] = useSearchParams(queryParams);

    //states
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        status: QueryBuilderHelper.DefaultQueryFilterValue('status') ?? 0,
        rolePublicId: QueryBuilderHelper.DefaultQueryFilterValue('rolePublicId'),
        sortBy: QueryBuilderHelper.DefaultSortValue(AdminUserListField.NAME),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
    });
    const [openDelete, setopenDelete] = useState(false);
    let [toDeleteItem, settoDeleteItem] = useState<any>(null);

    const q_queryKey = [CachingEnum.ADMIN_USERS, queryStr]
    const { data, fetchStatus, deleteAdminUserById } = useAdminUserHook(q_queryKey, queryStr);

    const tableColumns = [
        {
            accessor: AdminUserListField.NAME,
            title: 'POC Name',
            sortable: true,
            render: (record: AdminUserList) => (record?.[AdminUserListField.PUBLIC_ID] ? 
                <NewTab 
                linkTitle={record?.[AdminUserListField.NAME] != null && record?.[AdminUserListField.NAME] != "" ? record?.[AdminUserListField.NAME] : "--"}
                linkUrl={`${RouteEnum.ADMIN_USER_DETAILS}?id=${record?.[AdminUserListField.PUBLIC_ID] }&view=${ViewType.PREVIEW}`} /> : "--"
            ),
        },
        {
            accessor: AdminUserListField.USERNAME,
            title: 'Username',
            sortable: true,
            render: (record: AdminUserList) => record?.[AdminUserListField.USERNAME] ?? "--"
        },
        {
            accessor: AdminUserListField.ROLE_NAME,
            title: 'Role',
            sortable: true,
            render: (record: AdminUserList) => record?.[AdminUserListField.ROLE_NAME] ?? "--"
        },
        {
            accessor: AdminUserListField.CREATED_DATE,
            sortable: true,
            render: (record: AdminUserList) => record?.[AdminUserListField.CREATED_DATE] ? DateHelper.getFormattedDate(record?.[AdminUserListField.CREATED_DATE], "dd/MM/yyyy hh:mm a") : "--",
        },
        {
            accessor: AdminUserListField.UPDATED_DATE,
            sortable: true,
            title: 'Last Updated',
            render: (record: AdminUserList) => record?.[AdminUserListField.UPDATED_DATE] ? DateHelper.getFormattedDate(record?.[AdminUserListField.UPDATED_DATE], "dd/MM/yyyy hh:mm a") : "--",
        },
        {
            accessor: AdminUserListField.STATUS,
            sortable: true,
            render: (record: AdminUserList) => StatusHelper.getAdminUserStatus(record?.[AdminUserListField.STATUS] != ActiveStatusEnum.ALL ? record?.[AdminUserListField.STATUS] : ActiveStatusEnum.INACTIVE)
        },
        {
            accessor: 'action',
            title: "Action",
            render: (record: AdminUserList) => <>
                <CustomIconButton toolTipName='View'
                    icon={<IconEye />}
                    onClick={() => {
                        navigate(`${RouteEnum.ADMIN_USER_DETAILS}?id=${record?.[AdminUserListField.PUBLIC_ID] }&view=${ViewType.PREVIEW}`, { replace: true })
                    }} />
                {AccessibilityHelper.hasAccess(AdminModules.ADMIN_USER_EDIT) && <CustomIconButton toolTipName='Edit'
                    icon={<IconEdit />}
                    onClick={() => {
                        navigate(`${RouteEnum.ADMIN_USER_DETAILS}?id=${record?.[AdminUserListField.PUBLIC_ID] }&view=${ViewType.EDIT}`, { replace: true })
                    }} /> }
                {AccessibilityHelper.hasAccess(AdminModules.ADMIN_USER_DELETE) && <CustomIconButton toolTipName='Delete'
                    icon={<IconTrash color='var(--buttonHoverBGDangerColor)' />}
                    onClick={() => {
                        setopenDelete(true);
                        settoDeleteItem(record);
                    }} /> }
            </>
        },
    ];

    useEffect(() => {
        setSearchParams(QueryBuilderHelper.CleanQueryDictionary(queryStr));
    }, [queryStr]);

    const rolesArr = [{ id: 'SysAdmin', name: 'System Admin' },
    { id: 'Vendor', name: 'Vendor Admin' },
    { id: 'UserAdmin', name: 'User Admin' },
    { id: 'BillingAdmin', name: 'Billing Admin' }] //temp for now; will check with BE team
    return <TableContainer elevation={2} $hadBoxShadow={true}>
        <CustomListHeader title={RouteHelper.getRouteDescription(RouteEnum.ADMIN_USER_CONTROL)}
            subTitle=""
            headerButtons={<>
                {AccessibilityHelper.hasAccess(AdminModules.ADMIN_USER_CREATE) && <CustomButton
                    btnType={ButtonType.PRIMARY}
                    title='Add User'
                    startIcon={<IconPlus />}
                    // isRoundedCorner={true}
                    onClick={() => {
                        navigate(`${RouteEnum.ADMIN_USER_DETAILS}?id=0&view=${ViewType.ADD}`, { replace: true })
                    }} /> }
            </>} />
        <CustomListSearch searchValue={queryStr.searchString ?? ""}
            onChangeSearch={(searchString) => setQueryStr(prev => ({ ...prev, searchString: searchString, pageNumber: 1 }))}
            searchPlaceholder='Search Name / Username'
            legends={data?.legends}
            onClickLegendItem={(id) => setQueryStr(prev => ({ ...prev, status: id, pageNumber: 1 }))}>

            <RolesDropdown {...{
                name: "rolePublicId",
                title: 'Role',
                hideLabel: true,
                placeholder: 'Roles',
                fieldValue: queryStr?.rolePublicId ?? "",
                handleOnChange: (selectedValue: any) => {
                    setQueryStr(prev => ({
                        ...prev,
                        rolePublicId: selectedValue?.id ?? null,
                        pageNumber: 1,
                    }))
                },
            }} />

            {/*<FormDropdown name="roleDropdown"
                fullWidth
                placeholder='- All Roles -'
                options={rolesArr}
                fieldValue={rolesArr.find(x => x.id == queryStr?.rolePublicId)?.name ?? null}
                handleOnChange={(x: any | null) => {
                    setQueryStr(prev => ({
                        ...prev,
                        rolePublicId: x?.id ?? null
                    }))
                }} />*/}
            <CustomListSearchResetBtns onClickSearch={() => { }}
                onClickReset={() => {
                    setQueryStr(prev => ({
                        ...prev, searchString: '',
                        rolePublicId: null,
                        pageNumber: 1,
                    }))
                }} />
        </CustomListSearch>
        <CustomListComponent
            fetching={fetchStatus == "fetching"}
            columns={tableColumns}
            idAccessor={(record: AdminUserList) => `adminUser${record?.[AdminUserListField.PUBLIC_ID]}`}
            records={data?.data?.list ?? []}
            totalRecords={data?.data?.metaData?.totalCount ?? 0}
            page={queryStr.pageNumber}
            recordsPerPage={queryStr.pageSize}
            defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
            onSortStatusChange={((sort: any) => { setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 })) })}
            setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
            onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))}
           />

        {openDelete && <CustomDeleteConfirmation
            openDialog={openDelete}
            showRemarksField={true}
            handleClose={() => {
                setopenDelete(false);
            }}
            deleteItem={async (remarks: any) => {
                if(toDeleteItem?.[AdminUserListField.PUBLIC_ID]) {
                    let deleteReponse: any = await deleteAdminUserById(toDeleteItem?.[AdminUserListField.PUBLIC_ID], remarks);

                    if(deleteReponse?.isSuccess) {
                        setopenDelete(false);
                        // navigate(RouteEnum.COMPANIES, { replace: true });
                    }
                }
            }} />
        }
    </TableContainer>
}
